/** Individual account (profile, password, email replacement) copy. Part of `UI_STRINGS` - see ui-strings.ts. */
export const UI_ACCOUNT_STRINGS = {
  PROFILE: {
    // Matches the Workspace menu label so it is never confused with the Startup
    // Profile (UX sheet, StartupAdminVF r72).
    PAGE_TITLE: 'Individual Profile',
    EDIT_PROFILE: 'Edit Profile',
    CHANGE_PASSWORD: 'Change Password',
    SECTION_PERSONAL: 'Personal Details',
    SECTION_PASSWORD: 'Password',
    FIELD_FULL_NAME: 'Full Name',
    FIELD_EMAIL: 'Email',
    FIELD_PHONE: 'Phone',
    // A-50 (MYN-175): the Individual's Country replaces LinkedIn, which the
    // client's field sheet scopes to Investors and Trusted Partners. Their own
    // LinkedIn fields are separate models and are deliberately untouched.
    FIELD_COUNTRY: 'Country',
    FIELD_PASSWORD: 'Password',
    NOT_ADDED: 'Not added',
    PASSWORD_MASKED: '••••••••••',
    COMING_SOON_TITLE: 'Profile coming soon',
    COMING_SOON_DESC: 'This profile view is being built for your role.',
    // Edit page (MYN-27, Figma node 176:471)
    CANCEL: 'Cancel',
    SAVE_CHANGES: 'Save Changes',
    SAVING: 'Saving…',
    UPLOAD_PHOTO: 'Upload Photo',
    // Avatar upload (MYN-175). Failure copy is deliberately split: a too-large
    // file is the person's to fix, any other failure (wrong type, a failed S3
    // PUT, a refused presign) is ours and only says "try again".
    PHOTO_TOO_LARGE: 'That image is too large. Please choose a file under 5MB.',
    // Names the accepted format and size so the user can tell whether the file
    // was the problem (UX sheet, StartupAdminVF r73).
    PHOTO_UPLOAD_ERROR: 'We could not upload that photo. Please use a PNG or JPG under 5MB and try again.',
    // Individual persona only (FR-14, MYN-76): a self-service path (Update Email)
    // sits right next to this hint, so "contact support" would be wrong. UX sheet r74.
    EMAIL_LOCKED_HINT_INDIVIDUAL:
      'To update your email to reflect your company domain, click on "Update Email." Note that you will only be able to do this once.',
    FULL_NAME_REQUIRED: 'Full name is required.',
    SAVE_ERROR: 'We could not save your changes. Please try again.',
    DANGER_ZONE: 'Danger Zone',
    DANGER_ZONE_BODY: 'Deleting your account is permanent and cannot be undone. All your data will be removed.',
    DELETE_ACCOUNT: 'Delete Account',
    // Change Password dialog
    CURRENT_PASSWORD: 'Current Password',
    NEW_PASSWORD: 'New Password',
    CONFIRM_NEW_PASSWORD: 'Confirm New Password',
    CURRENT_PASSWORD_REQUIRED: 'Current password is required.',
    NEW_PASSWORD_TOO_SHORT: 'New password must be at least 8 characters.',
    NEW_PASSWORD_COMPLEXITY: 'New password must contain at least one letter and one number.',
    CONFIRM_PASSWORD_REQUIRED: 'Please confirm your new password.',
    PASSWORD_MISMATCH: 'New password and confirmation do not match.',
    CHANGE_PASSWORD_ERROR: 'We could not change your password. Please try again.',
    // Delete Account confirmation
    DELETE_ACCOUNT_CONFIRM_TITLE: 'Delete your account?',
    DELETE_ACCOUNT_CONFIRM_BODY:
      'This is permanent and cannot be undone. All your data will be removed and you will be signed out.',
    DELETE_ACCOUNT_ERROR: 'We could not delete your account. Please try again.',
    // Update Email dialog (FR-14/FR-68, MYN-76) - the "Update Email" action
    // beside the locked Email field opens the guided double-OTP flow below.
    REPLACE_EMAIL: 'Update Email',
    REPLACE_EMAIL_NEW_EMAIL_STEP_TITLE: 'Update Your Email',
    REPLACE_EMAIL_NEW_EMAIL_LABEL: 'Company Email',
    REPLACE_EMAIL_NEW_EMAIL_HINT:
      'Use a company email address - personal and free email providers are not supported.',
    REPLACE_EMAIL_NEW_EMAIL_REQUIRED: 'Enter your new email address.',
    REPLACE_EMAIL_NEW_EMAIL_INVALID: 'Enter a valid email address.',
    REPLACE_EMAIL_SEND_CODE: 'Send Code',
    REPLACE_EMAIL_SENDING: 'Sending…',
    REPLACE_EMAIL_NOT_COMPANY_EMAIL_ERROR:
      'A company email is required. Personal and free email providers are not supported.',
    REPLACE_EMAIL_DUPLICATE_ERROR: 'We could not use that email address. Please try a different one.',
    REPLACE_EMAIL_ALREADY_USED_ERROR:
      'Your email has already been updated once. Contact support to change it again.',
    REPLACE_EMAIL_START_ERROR: 'We could not start the email replacement. Please try again.',
    REPLACE_EMAIL_CURRENT_OTP_STEP_TITLE: 'Verify Your Current Email',
    REPLACE_EMAIL_NEW_OTP_STEP_TITLE: 'Verify Your New Email',
    REPLACE_EMAIL_CODE_SENT_PREFIX: 'Enter the 6-digit code sent to ',
    REPLACE_EMAIL_CODE_LABEL: 'Verification code',
    REPLACE_EMAIL_CODE_REQUIRED: 'Enter the 6-digit code.',
    REPLACE_EMAIL_VERIFY: 'Verify & Continue',
    REPLACE_EMAIL_VERIFYING: 'Verifying…',
    REPLACE_EMAIL_OTP_INVALID_ERROR: 'That code is incorrect. Please try again.',
    REPLACE_EMAIL_OTP_LOCKED_ERROR: 'Too many incorrect attempts. Start over to request a new code.',
    REPLACE_EMAIL_REQUEST_EXPIRED_ERROR: 'This request has expired. Start over to request a new code.',
    REPLACE_EMAIL_VERIFY_CURRENT_ERROR: 'We could not verify your current email. Please try again.',
    REPLACE_EMAIL_VERIFY_NEW_ERROR: 'We could not complete the email swap. Please start over.',
    REPLACE_EMAIL_START_OVER: 'Start Over',
    REPLACE_EMAIL_SUCCESS_TITLE: 'Email Updated',
    REPLACE_EMAIL_SUCCESS_BODY:
      'Your account email has been updated. For your security, you will need to log in again with your new email.',
    // FR-222: the app entry keeps the label it was scanned with; re-scanning is optional.
    REPLACE_EMAIL_SUCCESS_AUTHENTICATOR:
      'Your authenticator app still works. It may still show your old email. To update it, choose "Scan a new QR code" on your profile.',
    REPLACE_EMAIL_SUCCESS_HANDOFF_PREFIX: "You'll also be joining ",
    REPLACE_EMAIL_SUCCESS_HANDOFF_SUFFIX: ' - check your invites to finish joining.',
    REPLACE_EMAIL_DONE: 'Log In Again',
    REPLACE_EMAIL_SUCCESS_TOAST: 'Your email has been updated. Please log in again.',
  },
  /**
   * Email preferences card on the account page (FR-164, MYN-120). One boolean
   * that governs admin-composed email only (A-82, A-156); the "always sent" list
   * keeps optional and transactional mail visibly distinct.
   */
  EMAIL_PREFERENCES: {
    SECTION_TITLE: 'Email preferences',
    TOGGLE_LABEL: 'Optional emails',
    TOGGLE_HINT:
      'Announcements and updates that the My NEXT Raise team chooses to send you. Turn this off to stop receiving them.',
    STATUS_ON: 'You receive optional emails.',
    STATUS_OFF: 'You have opted out of optional emails.',
    SAVING: 'Saving…',
    LOADING: 'Loading email preferences',
    LOAD_ERROR: 'We could not load your email preferences.',
    SAVED_OPTED_OUT: 'You will no longer receive optional emails.',
    SAVED_OPTED_IN: 'You will receive optional emails again.',
    SAVE_ERROR: 'We could not save your email preference. Your previous choice is unchanged.',
    RETRY: 'Try again',
    ALWAYS_HEADING: 'Always sent, whatever you choose',
    ALWAYS_INTRO:
      'These emails are part of how the platform works, so this setting does not affect them.',
    ALWAYS_ITEMS: [
      'Security codes',
      'Invitations',
      'Billing notices',
      'Event notices',
      'Moderation notices',
    ],
  },
} as const;
